/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table } from "@tanstack/react-table";
import { debounce } from "lodash";
import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Label } from "components/ui/label";
import { useFilterActions } from "../ducks/hooks/action";
import { useFilterState } from "../ducks/hooks/state";
import { DataTableViewOptions } from "./data-table-view-options";
import SelectFilters from "./select-filters";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

type FilterKeys =
  | "supplier_name"
  | "cnpj"
  | "supplier_number"
  | "created_at"
  | "search"
  | "meta";

export function DataTableToolbar<TData>({
  table,
}: DataTableToolbarProps<TData>) {
  const { updateFilter, clearFilters } = useFilterActions();
  const filterState = useFilterState();

  const [searchValue, setSearchValue] = useState(
    filterState.filters.search || "",
  );

  const debouncedSearchUpdate = useCallback(
    debounce((value: string) => {
      updateFilter("search", value);
    }, 500),
    [],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setSearchValue(inputValue);

    const normalizedValue = inputValue.replace(/[^\w\s]/g, "");
    debouncedSearchUpdate(normalizedValue);
  };

  useEffect(() => {
    setSearchValue(filterState.filters.search || "");
  }, [filterState.filters.search]);

  const handleFilterChange = (key: FilterKeys, value: string) => {
    updateFilter(key, value === "all" ? undefined : value);
  };

  return (
    <div className="flex flex-wrap items-center gap-4 w-full">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="search-input">Buscar</Label>
          <Input
            id="search-input"
            placeholder="Fornecedor ou CNPJ..."
            value={searchValue}
            onChange={handleSearchChange}
            className="h-8 w-[150px] lg:w-[200px]"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="supplier-number">Número do Fornecedor</Label>
          <Input
            id="supplier-number"
            placeholder="Digite o número..."
            value={filterState.filters.supplier_number || ""}
            onChange={(e) =>
              handleFilterChange("supplier_number", e.target.value)
            }
            className="h-8 w-[150px] lg:w-[200px]"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="created-at">Criado em:</Label>
          <SelectFilters />
        </div>

        {Object.keys(filterState.filters).some(
          (k) => filterState.filters[k as keyof typeof filterState.filters],
        ) && (
          <Button variant="outline" onClick={clearFilters} className="h-8">
            <X />
            <span className="font-medium text-sm">Resetar Filtros</span>
          </Button>
        )}
      </div>

      <div className="ml-auto">
        <DataTableViewOptions table={table} />
      </div>
    </div>
  );
}
